import * as PIXI from 'pixi.js';
import { AudioSystem } from '../assets/AudioSystem';

interface CreditsPageOptions {
  onClose?: () => void;
  audioSystem?: AudioSystem | null;
}

interface CreditEntry {
  text: string;
  size: number;
  color: number;
  special?: boolean; // For special entries like "Lax"
}

export class CreditsPage {
  private container: PIXI.Container;
  private visible: boolean = false;
  private creditsContainer: PIXI.Container;
  private credits: CreditEntry[] = [];
  private creditTexts: PIXI.Text[] = [];
  private animationSpeed: number = 1.5; // pixels per frame
  private options: CreditsPageOptions;
  private ticker?: PIXI.Ticker;

  constructor(options: CreditsPageOptions = {}) {
    this.options = options;
    this.container = new PIXI.Container();
    this.creditsContainer = new PIXI.Container();
    this.setupPage();
    this.container.visible = false;
  }

  private setupPage(): void {
    const screenWidth = 1920;
    const screenHeight = 1080;

    // Backdrop
    const backdrop = new PIXI.Graphics();
    backdrop.beginFill(0x000000, 0.98);
    backdrop.drawRect(0, 0, screenWidth, screenHeight);
    backdrop.endFill();
    backdrop.eventMode = 'static';
    backdrop.cursor = 'pointer';
    // Right-click to close
    backdrop.on('rightdown', () => {
      this.options.audioSystem?.playSound('button_click');
      this.hide();
    });
    this.container.addChild(backdrop);

    // Credits container (will scroll)
    this.creditsContainer.x = screenWidth / 2;
    this.container.addChild(this.creditsContainer);

    // Define credits
    this.credits = [
      { text: 'MAZING HUNTERS', size: 42, color: 0xffd700 },
      { text: '', size: 20, color: 0xffffff },
      { text: 'Development Team', size: 32, color: 0xffe082 },
      { text: '', size: 20, color: 0xffffff },
      { text: 'Game Design', size: 28, color: 0xcccccc },
      { text: 'Development Team', size: 24, color: 0xffffff },
      { text: '', size: 20, color: 0xffffff },
      { text: 'Programming', size: 28, color: 0xcccccc },
      { text: 'Development Team', size: 24, color: 0xffffff },
      { text: '', size: 20, color: 0xffffff },
      { text: 'Art & Design', size: 28, color: 0xcccccc },
      { text: 'Development Team', size: 24, color: 0xffffff },
      { text: '', size: 30, color: 0xffffff },
      { text: 'Special Thanks', size: 32, color: 0xffe082 },
      { text: '', size: 20, color: 0xffffff },
      { text: 'Lax - the almighty god of Mazing Hunters', size: 36, color: 0xff00ff, special: true },
      { text: '', size: 40, color: 0xffffff },
      { text: 'Thank you for playing!', size: 28, color: 0x88ccff },
      { text: '', size: 60, color: 0xffffff }, // Extra space for loop
    ];

    // Create credit text objects
    let currentY = screenHeight + 50; // Start below screen
    for (const credit of this.credits) {
      if (credit.text === '') {
        currentY += credit.size;
        continue;
      }

      const textStyle: any = {
        fontFamily: 'Courier New',
        fontSize: credit.size,
        fill: credit.color,
        fontWeight: credit.special ? 'bold' : 'normal',
        align: 'center',
      };
      if (credit.special) {
        textStyle.stroke = 0x000000;
        textStyle.strokeThickness = 3;
      }
      const text = new PIXI.Text(credit.text, textStyle);
      text.anchor.set(0.5, 0);
      text.y = currentY;
      this.creditsContainer.addChild(text);
      this.creditTexts.push(text);
      currentY += credit.size + (credit.special ? 20 : 15);
    }

    // Close button (always visible)
    const closeButton = new PIXI.Graphics();
    closeButton.beginFill(0x006600, 0.9);
    closeButton.drawRoundedRect(0, 0, 250, 50, 8);
    closeButton.endFill();
    closeButton.lineStyle(2, 0x00aa00);
    closeButton.drawRoundedRect(0, 0, 250, 50, 8);
    closeButton.x = screenWidth - 280;
    closeButton.y = screenHeight - 70;
    closeButton.eventMode = 'static';
    closeButton.cursor = 'pointer';
    closeButton.on('pointerdown', () => {
      this.stopAnimation();
      this.options.audioSystem?.playSound('button_click');
      this.hide();
    });
    this.container.addChild(closeButton);

    const closeText = new PIXI.Text('Close', {
      fontFamily: 'Courier New',
      fontSize: 24,
      fill: 0xffffff,
      fontWeight: 'bold',
    });
    closeText.x = closeButton.x + closeButton.width / 2 - closeText.width / 2;
    closeText.y = closeButton.y + closeButton.height / 2 - closeText.height / 2;
    this.container.addChild(closeText);
  }

  private animateCredits = (): void => {
    if (!this.visible) {
      this.stopAnimation();
      return;
    }

    // Move all credits up
    for (const text of this.creditTexts) {
      text.y -= this.animationSpeed;

      // If credit has scrolled past top, reset to bottom
      const screenHeight = 1080;
      if (text.y + text.height < 0) {
        // Find the last credit's position
        let maxY = -Infinity;
        for (const t of this.creditTexts) {
          if (t.y > maxY) maxY = t.y;
        }
        text.y = maxY + 100; // Place after last credit
      }
    }
  };

  private stopAnimation(): void {
    if (this.ticker) {
      this.ticker.remove(this.animateCredits);
      this.ticker = undefined;
    }
  }

  show(): void {
    this.visible = true;
    this.container.visible = true;
    
    // Enable pointer events on the info pages canvas
    if ((window as any).infoPagesCanvas) {
      (window as any).infoPagesCanvas.style.pointerEvents = 'auto';
    }
    
    // Reset positions
    const screenHeight = 1080;
    let currentY = screenHeight + 50;
    for (let i = 0; i < this.credits.length; i++) {
      const credit = this.credits[i];
      if (credit.text === '') {
        currentY += credit.size;
        continue;
      }
      
      const text = this.creditTexts.find(t => t.text === credit.text);
      if (text) {
        text.y = currentY;
        currentY += credit.size + (credit.special ? 20 : 15);
      }
    }
    
    // Start animation using PIXI ticker
    this.ticker = PIXI.Ticker.shared;
    this.ticker.add(this.animateCredits);
  }

  hide(): void {
    this.stopAnimation();
    this.visible = false;
    this.container.visible = false;
    // Disable pointer events when hidden
    if ((window as any).infoPagesCanvas) {
      (window as any).infoPagesCanvas.style.pointerEvents = 'none';
    }
    this.options.onClose?.();
  }

  getContainer(): PIXI.Container {
    return this.container;
  }
}

